import { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { listTimeZones } from '../../lib/timeZones';

/**
 * An IANA time-zone picker: type "lisb" and pick Europe/Lisbon.
 *
 * May start empty (a new group, or an old one that never had a zone) so the
 * host has to choose where the trip happens — pre-filling the host's own zone
 * silently files a Lisbon weekend planned from Chicago under Chicago.
 *
 * Not clearable once chosen, on purpose. The group update stores `COALESCE(@TimeZone,
 * time_zone)`, so sending nothing keeps the old zone and can never unset it —
 * a clear button would look like it worked and change nothing.
 */
export default function TimeZonePicker({
  value,
  onChange,
  label = 'Time zone',
  helperText,
  disabled,
  required,
  error,
}: {
  /** The saved zone, or '' when none has been chosen yet. */
  value: string;
  onChange: (zone: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
}) {
  const options = useMemo(() => listTimeZones(value), [value]);
  return (
    <Autocomplete
      options={options}
      value={value || null}
      // Clearable only while nothing is chosen, which shows no clear button
      // anyway; once a zone is set it can be changed but never emptied.
      disableClearable={Boolean(value)}
      disabled={disabled}
      onChange={(_event, next) => {
        if (next) onChange(next);
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={helperText} required={required} error={error} />
      )}
    />
  );
}
